import { formComponents as fc } from "@/features/i18n/formComponents";
import { captureError } from "@/lib/sentry";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { colors } from "@patient-watch/icons";
import Warning from "@/assets/openmoji/Warning";
import { useLogoutMutation } from "@/features/auth/api/useLogoutMutation";
import { commonComponents as cc } from "../common";
import Description from "../common/Description";

interface Props {
    title?: string;
    error: Error;
    reset: () => void;
}

const ErrorPage = (props: Props) => {
    captureError(props.error, { title: props.title });
    const router = useRouter();
    const queryClient = useQueryClient();
    const logout = useLogoutMutation();

    const handleRetry = () => {
        // Reset all queries to ensure fresh data
        queryClient.resetQueries();
        props.reset();
        router.invalidate();
    };

    // Get user-friendly error message
    const getUserFriendlyMessage = (errorMessage: string) => {
        if (errorMessage?.includes("Profile not found")) {
            return "We're setting up your profile. Please try refreshing the page.";
        }
        if (errorMessage?.includes("Not authenticated")) {
            return "Your session has expired. Please log in again.";
        }
        if (errorMessage?.includes("No organisation found")) {
            return "Your account hasn't been set up yet. Please contact your administrator.";
        }
        return errorMessage || "An unexpected error occurred";
    };

    const errorMessage = getUserFriendlyMessage(props.error?.message);

    return (
        <section className="mx-auto p-6 max-w-2xl">
            <cc.Title words={props.title ?? "Something went wrong"} />
            <Warning fill={colors["amber-400"]} className="flex mx-auto w-32 h-32" />
            <div className="flex flex-col justify-center items-center gap-6 mx-auto p-4 w-full text-center">
                <Description className="text-lg">{errorMessage}</Description>
                <Description className="text-md text-slate-600">
                    We apologize for the inconvenience. You can try refreshing the page or logging out and back in.
                </Description>
                <div className="flex flex-col gap-4 w-full max-w-sm">
                    <fc.RetryButton
                        variant="default"
                        size="lg"
                        onClick={handleRetry}
                        className="w-full"
                        label="Refresh Page & Retry"
                    />
                    <fc.CancelButton
                        variant="outline"
                        size="lg"
                        className="w-full"
                        label="Logout & Start Fresh"
                        onClick={() => logout.mutate()}
                    />
                </div>
                <Description className="text-slate-500 text-sm">
                    If the problem persists, please contact us at{" "}
                    <a className="text-indigo-600 hover:text-indigo-700" href="mailto:support@patient-watch.com">
                        support@patient-watch.com
                    </a>
                </Description>
            </div>
        </section>
    );
};

export default ErrorPage;
